import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiArrowRight, FiTrendingUp, FiClock, FiDollarSign } = FiIcons;

const TestimonialSection = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-white to-blue-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto"
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2 mb-6">
              <SafeIcon icon={FiTrendingUp} className="text-green-600" />
              <span className="text-sm font-semibold text-green-700">Real Results from Real Coaches</span>
            </div>

            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              How a Business Consultant Added{' '}
              <span className="text-green-600">$60,000</span> in Monthly Revenue
            </h2>
          </div>

          {/* Main Testimonial Card */}
          <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8 lg:p-12 rounded-3xl shadow-2xl border-2 border-blue-200 mb-12 relative overflow-hidden">
            {/* Decorative Quote Mark */}
            <div className="absolute top-0 right-0 text-blue-200 text-9xl font-serif opacity-20">"</div>

            <div className="grid lg:grid-cols-3 gap-8 items-center relative z-10">
              {/* Profile Section */}
              <div className="text-center lg:text-left">
                <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mx-auto lg:mx-0 mb-4 flex items-center justify-center shadow-xl transform hover:rotate-3 transition-transform">
                  <span className="text-white text-5xl font-bold">JC</span>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-2">John Carter</h3>
                <p className="text-blue-600 font-semibold mb-1">Carter Consulting</p>
                <p className="text-sm text-gray-600 mb-4">Business Strategy Consultant</p>
                
                <div className="flex justify-center lg:justify-start gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-xl fill-current" />
                  ))}
                </div>

                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <p className="text-xs text-gray-500 mb-1">Investment</p>
                  <p className="font-bold text-gray-900">$1,000</p>
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="lg:col-span-2">
                <blockquote className="text-xl lg:text-2xl text-gray-700 leading-relaxed mb-8 font-medium">
                  "I was <span className="text-red-600 font-bold">drowning in discovery calls</span>. I invested $1,000 in the AI Assistant from Handvantage. 
                  It cut my calls from <span className="text-red-600 font-bold">10 a week to just 4</span>, but I started closing 
                  <span className="text-green-600 font-bold"> 3 of them instead of 1</span>. 
                  
                  <br/><br/>
                  
                  I saved <span className="text-blue-600 font-bold">12 hours a week</span> and added 
                  <span className="text-green-600 font-bold"> $60,000 in new monthly revenue</span>. 
                  It <span className="font-bold text-purple-600">paid for itself in 3 days</span>."
                </blockquote>

                {/* Results Grid */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-5 rounded-xl shadow-lg border-2 border-green-200 hover:scale-105 transition-transform">
                    <SafeIcon icon={FiClock} className="text-4xl text-green-600 mb-3 mx-auto" />
                    <p className="text-3xl font-bold text-green-600 mb-1 text-center">12 Hours</p>
                    <p className="text-sm text-gray-600 text-center">Saved per week</p>
                  </div>
                  
                  <div className="bg-white p-5 rounded-xl shadow-lg border-2 border-blue-200 hover:scale-105 transition-transform">
                    <SafeIcon icon={FiTrendingUp} className="text-4xl text-blue-600 mb-3 mx-auto" />
                    <p className="text-3xl font-bold text-blue-600 mb-1 text-center">3x Close Rate</p>
                    <p className="text-sm text-gray-600 text-center">From 1 to 3 clients/week</p>
                  </div>
                  
                  <div className="bg-white p-5 rounded-xl shadow-lg border-2 border-purple-200 hover:scale-105 transition-transform">
                    <SafeIcon icon={FiDollarSign} className="text-4xl text-purple-600 mb-3 mx-auto" />
                    <p className="text-3xl font-bold text-purple-600 mb-1 text-center">$60K</p>
                    <p className="text-sm text-gray-600 text-center">New monthly revenue</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ROI Highlight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-2xl shadow-2xl text-center mb-12"
          >
            <h3 className="text-3xl font-bold mb-4">The ROI is Undeniable</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-5xl font-bold mb-2">$1,000</p>
                <p className="text-green-100">One-time investment</p>
              </div>
              <div>
                <p className="text-5xl font-bold mb-2">3 Days</p>
                <p className="text-green-100">To pay for itself</p>
              </div>
              <div>
                <p className="text-5xl font-bold mb-2">$720K</p>
                <p className="text-green-100">Added annually</p>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <div className="text-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-12 py-6 rounded-xl text-2xl font-bold hover:shadow-2xl transition-all duration-300 inline-flex items-center gap-3 group"
            >
              GET RESULTS LIKE JOHN
              <SafeIcon icon={FiArrowRight} className="text-2xl group-hover:translate-x-2 transition-transform" />
            </motion.button>
            
            <p className="text-sm text-gray-600 mt-6">
              Join <span className="font-bold text-gray-900">200+ coaches & consultants</span> who've automated their lead qualification
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialSection;